import { useMemo, useState } from 'react';
import { useApolloClient, useMutation, useQuery } from '@apollo/client/react';
import { Link, useNavigate } from 'react-router';
import { Plus, Receipt } from 'lucide-react';
import type { ColumnDef, RowSelectionState } from '@tanstack/react-table';
import { graphql } from '@/gql';
import type { TabAction, TabStatus, TabStatusGroup } from '@/gql/graphql';
import { Badge, type BadgeTone } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { DataGrid } from '@/components/ui/DataGrid';
import { FilterBar } from '@/components/ui/FilterBar';
import { FormField } from '@/components/ui/FormLayout';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { Switch } from '@/components/ui/Switch';
import { formatCurrency, formatDateTime, formatShortId } from '@/lib/format';
import { downloadAuthenticatedCsv } from '@/lib/downloadCsv';
import { useListHref } from '@/hooks/useListLocation';
import { useDebouncedUrlField, useUrlState } from '@/hooks/useUrlState';
import { tabsUrlSchema } from './tabsUrlState';
import {
  STAFF_RECEIPT_GRID_BUTTON_CLASS,
  openStaffTabReceipt,
} from '@/lib/staffReceiptDownload';
import { TAB_BULK_ACTIONS, TAB_ACTION_LABELS, tabActionLabel, tabActionVariant } from './tabActions';

const TabsDoc = graphql(/* GraphQL */ `
  query Tabs(
    $filter: TabFilter
    $status: TabStatusGroup
    $page: Int
    $pageSize: Int
    $sort: TabSort
  ) {
    tabs(filter: $filter, status: $status, page: $page, pageSize: $pageSize, sort: $sort) {
      nodes {
        id
        name
        status
        createdAt
        updatedAt
        grandTotal
        amountPaid
        amountDue
        allowedActions
        table {
          id
          name
        }
      }
      pageInfo {
        page
        pageSize
        totalRows
        totalPages
      }
    }
  }
`);

const ExportTabsCsvDoc = graphql(/* GraphQL */ `
  query ExportTabsCsv($filter: TabFilter, $status: TabStatusGroup, $sort: TabSort) {
    exportTabsCsv(filter: $filter, status: $status, sort: $sort) {
      url
      expiresAt
    }
  }
`);

const BulkPerformTabActionDoc = graphql(/* GraphQL */ `
  mutation BulkPerformTabAction($ids: [ID!]!, $action: TabAction!) {
    bulkPerformTabAction(ids: $ids, action: $action) {
      succeeded
      failed {
        id
        reason
      }
    }
  }
`);

const CreateTabDoc = graphql(/* GraphQL */ `
  mutation CreateTab($input: CreateTabInput!) {
    createTab(input: $input) {
      id
      name
      isInvoice
      exactAccountId
    }
  }
`);

const ExactAccountsDoc = graphql(/* GraphQL */ `
  query ExactAccountsForTab {
    exactAccounts {
      id
      code
      name
    }
  }
`);

interface TabRow {
  id: string;
  name: string;
  status: TabStatus;
  createdAt: string;
  updatedAt: string;
  grandTotal: number;
  amountPaid: number;
  amountDue: number;
  allowedActions: TabAction[];
  table?: { id: string; name: string } | null;
}

const STATUS_TONE: Record<TabStatus, BadgeTone> = {
  OPEN: 'info',
  CLOSED: 'neutral',
  FULL_PAID: 'success',
  PARTIAL_PAID: 'warning',
  FULL_REFUNDED: 'warning',
  CANCELED: 'neutral',
  LOSS: 'error',
};

/**
 * Two-grid tabs overview. The UNPAID grid shows every tab with an outstanding
 * balance (amountDue > 0). The OTHERS grid shows the rest — closed or
 * refunded. A single search and filter bar drives both grids.
 */
export function TabsListPage() {
  const navigate = useNavigate();
  const apollo = useApolloClient();
  const { state: url, patch } = useUrlState(tabsUrlSchema);
  const listHref = useListHref();
  const [search, setSearch] = useDebouncedUrlField(url.search, (next) =>
    patch({ search: next, unpaidPage: 1, othersPage: 1 }),
  );
  const [createOpen, setCreateOpen] = useState(false);
  const [newTabName, setNewTabName] = useState('');
  const [newTabIsInvoice, setNewTabIsInvoice] = useState(false);
  const [newTabExactAccountId, setNewTabExactAccountId] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);
  const [createTab, { loading: creating }] = useMutation(CreateTabDoc);
  const exactAccountsQuery = useQuery(ExactAccountsDoc, {
    skip: !createOpen || !newTabIsInvoice,
  });

  const openCreate = () => {
    setNewTabName('');
    setNewTabIsInvoice(false);
    setNewTabExactAccountId('');
    setCreateError(null);
    setCreateOpen(true);
  };

  const submitCreate = async (event: React.FormEvent) => {
    event.preventDefault();
    const name = newTabName.trim();
    if (!name || creating) return;
    if (newTabIsInvoice && !newTabExactAccountId) {
      setCreateError('Exact Account is required for invoice tabs.');
      return;
    }

    setCreateError(null);
    try {
      const { data: result } = await createTab({
        variables: {
          input: {
            name,
            isInvoice: newTabIsInvoice || null,
            exactAccountId: newTabIsInvoice ? newTabExactAccountId : null,
          },
        },
      });
      setCreateOpen(false);
      await apollo.refetchQueries({ include: [TabsDoc] });
      const id = result?.createTab?.id;
      if (id) {
        navigate(`/tabs/${id}`, { state: { from: listHref } });
      }
    } catch (error) {
      setCreateError(error instanceof Error ? error.message : 'Could not create tab.');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
            Tabs
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Running tabs with an outstanding balance are shown first. Closed and
            refunded tabs follow below.
          </p>
        </div>
        <Button id="admin.tabs.new" onClick={openCreate} leftIcon={<Plus className="h-4 w-4" />}>
          New tab
        </Button>
      </div>
      <FilterBar
        search={{
          id: 'admin.tabs.search',
          value: search,
          onChange: setSearch,
          placeholder: 'Search name or id…',
        }}
      />
      <TabSection
        title="Unpaid"
        statusGroup="UNPAID"
        search={url.search}
        page={url.unpaidPage}
        pageSize={url.unpaidSize}
        onPageChange={(next) => patch({ unpaidPage: next })}
        onPageSizeChange={(next) => patch({ unpaidSize: next, unpaidPage: 1 })}
        csvExportButton
      />
      <TabSection
        title="Closed & canceled"
        statusGroup="OTHERS"
        search={url.search}
        page={url.othersPage}
        pageSize={url.othersSize}
        onPageChange={(next) => patch({ othersPage: next })}
        onPageSizeChange={(next) => patch({ othersSize: next, othersPage: 1 })}
      />

      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="New tab"
      >
        <form onSubmit={submitCreate} className="space-y-4">
          <FormField label="Name" htmlFor="new-tab-name" required>
            <Input
              id="new-tab-name"
              value={newTabName}
              onChange={(event) => setNewTabName(event.target.value)}
              placeholder="Tab name"
              autoFocus
              required
            />
          </FormField>
          <Switch
            id="new-tab-is-invoice"
            label="Invoice tab"
            description="Settled as invoice at daily cutoff and posted to Exact as an Open sales invoice."
            checked={newTabIsInvoice}
            onChange={(event) => {
              const checked = event.target.checked;
              setNewTabIsInvoice(checked);
              if (!checked) setNewTabExactAccountId('');
            }}
          />
          {newTabIsInvoice && (
            <FormField label="Exact Account" htmlFor="new-tab-exact-account" required>
              <Select
                id="new-tab-exact-account"
                value={newTabExactAccountId}
                onChange={(event) => setNewTabExactAccountId(event.target.value)}
                required
                options={[
                  { value: '', label: exactAccountsQuery.loading ? 'Loading…' : 'Select debtor…' },
                  ...(exactAccountsQuery.data?.exactAccounts ?? []).map((a) => ({
                    value: a.id,
                    label: `${a.code} — ${a.name}`,
                  })),
                ]}
              />
              {exactAccountsQuery.error && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                  {exactAccountsQuery.error.message}
                </p>
              )}
            </FormField>
          )}
          {createError && (
            <p className="text-sm text-red-600 dark:text-red-400">{createError}</p>
          )}
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setCreateOpen(false)}
              disabled={creating}
            >
              Cancel
            </Button>
            <Button
              id="admin.tabs.create"
              type="submit"
              loading={creating}
              disabled={!newTabName.trim() || (newTabIsInvoice && !newTabExactAccountId)}
            >
              Create tab
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

function TabSection({
  title,
  statusGroup,
  search,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  csvExportButton,
}: {
  title: string;
  statusGroup: TabStatusGroup;
  search: string;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  csvExportButton?: boolean;
}) {
  const apollo = useApolloClient();
  const listHref = useListHref();
  const [selection, setSelection] = useState<RowSelectionState>({});
  const [feedback, setFeedback] = useState<string | null>(null);
  const [bulkPerformTabAction] = useMutation(BulkPerformTabActionDoc);

  const filter = useMemo(() => ({ search: search || null }), [search]);
  const sort = useMemo(
    () => ({ field: 'UPDATED_AT' as const, direction: 'DESC' as const }),
    [],
  );

  const { data, previousData, loading } = useQuery(TabsDoc, {
    variables: { filter, status: statusGroup, page, pageSize, sort },
  });
  const tabsData = data ?? previousData;

  const rows: TabRow[] = useMemo(
    () =>
      (tabsData?.tabs.nodes ?? []).map((n) => ({
        id: n.id,
        name: n.name,
        status: n.status as TabStatus,
        createdAt: n.createdAt,
        updatedAt: n.updatedAt,
        grandTotal: n.grandTotal,
        amountPaid: n.amountPaid,
        amountDue: n.amountDue,
        allowedActions: n.allowedActions,
        table: n.table,
      })),
    [tabsData],
  );
  const pageInfo = tabsData?.tabs.pageInfo;

  const columns = useMemo<ColumnDef<TabRow>[]>(
    () => [
      {
        id: 'name',
        header: 'Name',
        cell: ({ row }) => (
          <Link
            to={`/tabs/${row.original.id}`}
            state={{ from: listHref }}
            className="font-medium text-gray-900 hover:text-brand dark:text-white"
          >
            {row.original.name || `#${formatShortId(row.original.id)}`}
          </Link>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <Badge tone={STATUS_TONE[row.original.status] ?? 'neutral'}>
            {row.original.status.replace('_', ' ').toLowerCase()}
          </Badge>
        ),
      },
      {
        id: 'table',
        header: 'Table',
        cell: ({ row }) => row.original.table?.name ?? '—',
      },
      {
        id: 'total',
        header: 'Total',
        cell: ({ row }) => formatCurrency(row.original.grandTotal),
      },
      {
        id: 'paid',
        header: 'Paid',
        cell: ({ row }) => formatCurrency(row.original.amountPaid),
      },
      {
        id: 'due',
        header: 'Due',
        cell: ({ row }) => (
          <span className={row.original.amountDue > 0 ? 'font-medium text-red-600' : ''}>
            {formatCurrency(row.original.amountDue)}
          </span>
        ),
      },
      {
        id: 'updatedAt',
        header: 'Updated',
        cell: ({ row }) => formatDateTime(row.original.updatedAt),
      },
    ],
    [listHref],
  );

  const handleExport = async () => {
    const { data: exportData } = await apollo.query({
      query: ExportTabsCsvDoc,
      variables: { filter, status: statusGroup, sort },
      fetchPolicy: 'network-only',
    });
    if (exportData?.exportTabsCsv?.url) {
      await downloadAuthenticatedCsv(exportData.exportTabsCsv.url);
    }
  };

  const runBulk = async (ids: string[], action: TabAction) => {
    if (ids.length === 0) return;
    setFeedback(null);
    try {
      const { data: result } = await bulkPerformTabAction({
        variables: { ids, action },
      });
      const failed = result?.bulkPerformTabAction.failed ?? [];
      if (failed.length > 0) {
        const first = failed[0]?.reason ?? 'Unknown error';
        setFeedback(
          `${failed.length} tab(s) failed during "${tabActionLabel(action)}". First error: ${first}`,
        );
      } else {
        setFeedback(`Applied "${tabActionLabel(action)}" to ${ids.length} tab(s).`);
      }
      await apollo.refetchQueries({ include: [TabsDoc] });
      setSelection({});
    } catch (error) {
      setFeedback(error instanceof Error ? error.message : 'Bulk action failed.');
    }
  };

  return (
    <section className="space-y-2">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
        {title}
        {pageInfo ? (
          <span className="ml-2 text-sm font-normal text-gray-500">
            ({pageInfo.totalRows.toLocaleString()})
          </span>
        ) : null}
      </h2>
      {feedback && (
        <div className="rounded-xl border border-brand-200 bg-brand-50 px-4 py-2 text-sm text-brand-700 dark:border-brand-800 dark:bg-brand-900/40 dark:text-brand-200">
          {feedback}
        </div>
      )}
      <DataGrid<TabRow, TabAction>
        data={rows}
        columns={columns}
        loading={loading && !tabsData}
        rowId={(row) => row.id}
        onExport={csvExportButton ? handleExport : undefined}
        selection={{ state: selection, onChange: setSelection }}
        allowedActionsForRow={(row) => row.allowedActions}
        actions={[
          ...TAB_BULK_ACTIONS.map((action) => ({
            id: action,
            label: tabActionLabel(action),
            variant: tabActionVariant(action),
            onRun: (selected: TabRow[]) =>
              runBulk(
                selected.map((row) => row.id),
                action,
              ),
          })),
          {
            id: 'DOWNLOAD_RECEIPT',
            label: TAB_ACTION_LABELS.DOWNLOAD_RECEIPT,
            variant: 'outline',
            icon: <Receipt className="h-4 w-4" aria-hidden />,
            buttonClassName: STAFF_RECEIPT_GRID_BUTTON_CLASS,
            isAllowed: (selected: TabRow[]) =>
              selected.length === 1 &&
              selected[0].allowedActions.includes('DOWNLOAD_RECEIPT'),
            onRun: async (selected: TabRow[]) => {
              setFeedback(null);
              try {
                await openStaffTabReceipt(apollo, selected[0].id);
              } catch (e) {
                setFeedback(e instanceof Error ? e.message : 'Receipt download failed.');
              }
            },
          },
        ]}
        pagination={{
          page: pageInfo?.page ?? page,
          pageSize: pageInfo?.pageSize ?? pageSize,
          totalRows: pageInfo?.totalRows ?? 0,
          totalPages: pageInfo?.totalPages ?? 1,
          onPageChange,
          onPageSizeChange,
        }}
      />
    </section>
  );
}
